import { browser } from '$app/environment';
import type { CategoryRead } from '$lib/api/generated/schemas/categoryRead';
import type { TrackerRead } from '$lib/api/generated/schemas/trackerRead';
import { DEFAULT_TRACKER_COLOR } from '$lib/constants/colors';
import type {
	CategoryNode,
	TrackerNode,
	TreeNode,
	PersistedState,
	DataTreeState,
	SidebarCategoryItem,
	SidebarTrackerItem
} from './dataTreeStore.types';

// Storage keys
export const STORAGE_KEYS = {
	EXPANDED_CATEGORIES: 'data-tree-expanded',
	FAVORITES: 'data-tree-favorites',
	CATEGORY_FAVORITES: 'data-tree-category-favorites',
	SETTINGS: 'data-tree-settings'
} as const;

// Default settings
export const DEFAULT_SETTINGS = {
	autoExpandOnNavigation: true,
	maxTreeDepth: 4
} as const;

// ===== PERSISTENCE UTILITIES =====

export function loadPersistedState(): Partial<PersistedState> {
	if (!browser) return {};

	try {
		clearPersistedState();
		const settings = localStorage.getItem(STORAGE_KEYS.SETTINGS);

		return {
			expandedCategories: [],
			favorites: [],
			favoriteCategories: [],
			settings: settings ? JSON.parse(settings) : DEFAULT_SETTINGS
		};
	} catch (error) {
		console.warn('Failed to load persisted state:', error);
		return {};
	}
}

export function savePersistedState(state: Partial<PersistedState>): void {
	if (!browser) return;

	try {
		clearPersistedState();
		if (state.settings) {
			localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(state.settings));
		}
	} catch (error) {
		console.warn('Failed to save persisted state:', error);
	}
}

export function clearPersistedState(): void {
	if (!browser) return;

	[
		STORAGE_KEYS.EXPANDED_CATEGORIES,
		STORAGE_KEYS.FAVORITES,
		STORAGE_KEYS.CATEGORY_FAVORITES
	].forEach((key) => {
		try {
			localStorage.removeItem(key);
		} catch (error) {
			console.warn(`Failed to clear ${key}:`, error);
		}
	});
}

// ===== DATA TRANSFORMATION UTILITIES =====

export function transformCategoryToNode(
	category: CategoryRead,
	level: number = 0,
	expandedCategories: Set<string> = new Set(),
	categoryTrackers: Map<string, TrackerNode[]> = new Map(),
	favoriteCategories: Set<string> = new Set()
): CategoryNode {
	const trackers = categoryTrackers.get(category.id) || [];
	const isExpanded = expandedCategories.has(category.id);

	return {
		...category,
		level,
		isExpanded,
		hasChildren: false, // Will be updated when building tree
		children: [],
		trackers,
		parentPath: [], // Will be updated when building tree
		href: `/app/journal?category=${category.id}`,
		trackerCount: 0, // Will be computed recursively in buildCategoryTree
		isFavorite: favoriteCategories.has(category.id)
	};
}

export function transformTrackerToNode(
	tracker: TrackerRead,
	isFavorite: boolean = false
): TrackerNode {
	return {
		id: tracker.id,
		name: tracker.name,
		description: tracker.description || undefined,
		category_id: tracker.category_id || undefined,
		color: tracker.effective_color || DEFAULT_TRACKER_COLOR,
		icon: tracker.icon || undefined,
		is_integration: tracker.is_integration,
		metrics: tracker.metrics || [],
		created_at: tracker.created_at,
		updated_at: tracker.updated_at,
		last_entry_date: tracker.last_entry_date || null,
		category: undefined, // Avoid circular references
		archived_at: tracker.archived_at || null,
		deleted_at: tracker.deleted_at || null,
		deletion_scheduled_at: tracker.deletion_scheduled_at || null,

		// UI state
		lastValue: undefined, // TODO: Get from last entry
		unit: tracker.metrics?.[0]?.unit || undefined,
		isFavorite,
		isLoading: false,
		error: null
	};
}

// ===== TREE BUILDING UTILITIES =====

export function buildCategoryTree(
	categories: CategoryRead[],
	expandedCategories: Set<string>,
	categoryTrackers: Map<string, TrackerNode[]>,
	favoriteCategories: Set<string>,
	currentPath?: string
): CategoryNode[] {
	// Create a map for fast parent lookups
	const categoryMap = new Map<string, CategoryNode>();

	// First pass: Transform all categories to nodes
	categories.forEach((cat) => {
		categoryMap.set(
			cat.id,
			transformCategoryToNode(cat, 0, expandedCategories, categoryTrackers, favoriteCategories)
		);
	});

	// Second pass: Build hierarchical structure
	const buildNodes = (
		parentId: string | null = null,
		level: number = 0,
		parentPath: string[] = []
	): CategoryNode[] => {
		return categories
			.filter((cat) => cat.parent_id === parentId)
			.map((cat) => {
				const node = categoryMap.get(cat.id)!;
				const currentParentPath = [...parentPath, cat.id];

				// Recursively build children
				const children = buildNodes(cat.id, level + 1, currentParentPath);

				// Get trackers for this category
				const trackers = categoryTrackers.get(cat.id) || [];

				// Update node properties
				node.level = level;
				node.children = children;
				node.trackers = trackers;
				node.parentPath = parentPath;
				node.hasChildren =
					children.length > 0 || trackers.length > 0 || (cat.active_tracker_count ?? 0) > 0;
				const directCount = trackers.length > 0 ? trackers.length : (cat.active_tracker_count ?? 0);
				const childrenCount = children.reduce((sum, child) => sum + child.trackerCount, 0);
				node.trackerCount = directCount + childrenCount;
				node.isFavorite = favoriteCategories.has(cat.id);

				// Auto-expand based on current path
				if (
					currentPath &&
					(currentPath.includes(cat.id) || currentPath.startsWith(`/category/${cat.id}`))
				) {
					node.isExpanded = true;
				}

				return node;
			})
			.sort((a, b) => a.name.localeCompare(b.name));
	};

	return buildNodes();
}

export function buildRenderTree(
	categoryNodes: CategoryNode[],
	uncategorizedTrackers: TrackerNode[] = []
): TreeNode[] {
	const buildTree = (nodes: CategoryNode[], level: number = 0): TreeNode[] => {
		const result: TreeNode[] = [];

		nodes.forEach((category) => {
			// Add category node
			const categoryTreeNode: TreeNode = {
				type: 'category',
				id: category.id,
				name: category.name,
				level,
				color: category.color || undefined,
				icon: category.icon || undefined,
				href: category.href,
				isExpanded: category.isExpanded,
				hasChildren: category.hasChildren,
				count: category.trackerCount,
				category
			};

			result.push(categoryTreeNode);

			// If expanded, add trackers first
			if (category.isExpanded && category.trackers.length > 0) {
				category.trackers.forEach((tracker) => {
					const trackerTreeNode: TreeNode = {
						type: 'tracker',
						id: tracker.id,
						name: tracker.name,
						level: level + 1,
						color: tracker.color,
						icon: tracker.icon,
						tracker,
						lastValue: tracker.lastValue,
						unit: tracker.unit,
						lastEntryDate: tracker.last_entry_date
					};

					result.push(trackerTreeNode);
				});
			}

			// Then add child categories recursively
			if (category.isExpanded && category.children.length > 0) {
				result.push(...buildTree(category.children, level + 1));
			}
		});

		return result;
	};

	const result = buildTree(categoryNodes);

	// Add uncategorized trackers at the bottom
	if (uncategorizedTrackers.length > 0) {
		// Add "Uncategorized" section header
		const uncategorizedNode: TreeNode = {
			type: 'category',
			id: 'uncategorized',
			name: 'Uncategorized',
			level: 0,
			color: '#6B7280', // Gray color for uncategorized
			icon: 'default',
			href: '/uncategorized',
			isExpanded: true, // Always expanded
			hasChildren: true,
			count: uncategorizedTrackers.length
		};

		result.push(uncategorizedNode);

		// Add uncategorized trackers
		uncategorizedTrackers.forEach((tracker) => {
			const trackerTreeNode: TreeNode = {
				type: 'tracker',
				id: tracker.id,
				name: tracker.name,
				level: 1,
				color: tracker.color || '#6B7280',
				icon: tracker.icon || 'default',
				tracker,
				lastValue: tracker.lastValue,
				unit: tracker.unit,
				lastEntryDate: tracker.last_entry_date
			};

			result.push(trackerTreeNode);
		});
	}

	return result;
}

// ===== COMPONENT COMPATIBILITY UTILITIES =====

export function convertToSidebarCategory(node: CategoryNode): SidebarCategoryItem {
	return {
		id: node.id,
		name: node.name,
		color: node.color ?? undefined,
		icon: node.icon ?? undefined,
		count: node.trackerCount,
		level: node.level,
		parentId: node.parent_id ?? undefined,
		isExpanded: node.isExpanded,
		hasChildren: node.hasChildren,
		href: node.href,
		children: node.children.map(convertToSidebarCategory),
		trackers: node.trackers.map(convertToSidebarTracker),
		isFavorite: node.isFavorite
	};
}

export function convertToSidebarTracker(tracker: TrackerNode): SidebarTrackerItem {
	return {
		id: tracker.id,
		name: tracker.name,
		color: tracker.color,
		icon: tracker.icon,
		lastValue: tracker.lastValue,
		unit: tracker.unit,
		lastEntryDate: tracker.last_entry_date,
		isFavorite: tracker.isFavorite || false
	};
}

// ===== SEARCH AND FILTER UTILITIES =====

export function searchInTree(
	categories: Map<string, CategoryNode>,
	trackers: Map<string, TrackerNode>,
	query: string
): { categories: CategoryNode[]; trackers: TrackerNode[] } {
	const normalizedQuery = query.toLowerCase().trim();

	if (!normalizedQuery) {
		return { categories: [], trackers: [] };
	}

	const matchingCategories: CategoryNode[] = [];
	const matchingTrackers: TrackerNode[] = [];

	// Search in categories
	categories.forEach((category) => {
		if (category.name.toLowerCase().includes(normalizedQuery)) {
			matchingCategories.push(category);
		}
	});

	// Search in trackers
	trackers.forEach((tracker) => {
		if (
			tracker.name.toLowerCase().includes(normalizedQuery) ||
			tracker.description?.toLowerCase().includes(normalizedQuery)
		) {
			matchingTrackers.push(tracker);
		}
	});

	return { categories: matchingCategories, trackers: matchingTrackers };
}

// ===== PATH AND NAVIGATION UTILITIES =====

export function getCategoryParents(
	categoryId: string,
	categories: Map<string, CategoryNode>
): CategoryNode[] {
	const parents: CategoryNode[] = [];
	let current = categories.get(categoryId);

	while (current?.parent_id) {
		const parent = categories.get(current.parent_id);
		if (parent) {
			parents.unshift(parent);
			current = parent;
		} else {
			break;
		}
	}

	return parents;
}

export function getCategoryPath(
	categoryId: string,
	categories: Map<string, CategoryNode>
): CategoryNode[] {
	const category = categories.get(categoryId);
	if (!category) return [];

	const parents = getCategoryParents(categoryId, categories);
	return [...parents, category];
}

// ===== VALIDATION UTILITIES =====

export function validateCategoryNode(node: any): node is CategoryNode {
	return (
		typeof node === 'object' &&
		node !== null &&
		typeof node.id === 'string' &&
		typeof node.name === 'string' &&
		typeof node.level === 'number' &&
		typeof node.isExpanded === 'boolean' &&
		typeof node.hasChildren === 'boolean' &&
		Array.isArray(node.children) &&
		Array.isArray(node.trackers)
	);
}

export function validateTrackerNode(node: any): node is TrackerNode {
	return (
		typeof node === 'object' &&
		node !== null &&
		typeof node.id === 'string' &&
		typeof node.name === 'string' &&
		typeof node.is_integration === 'boolean'
	);
}

// ===== DEBUGGING UTILITIES =====

export function logDataTreeState(state: DataTreeState, context: string = 'DataTreeStore'): void {
	if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
		console.group(`[${context}] State Debug`);
		console.log('Categories:', state.categories.size);
		console.log('Trackers:', state.trackers.size);
		console.log('Tree nodes:', state.tree.length);
		console.log('Expanded categories:', [...state.expandedCategories]);
		console.log('Favorites:', [...state.favorites]);
		console.log('Loading states:', {
			isLoading: state.isLoading,
			isCategoriesLoading: state.isCategoriesLoading,
			isTrackersLoading: state.isTrackersLoading
		});
		if (state.error) {
			console.error('Error:', state.error);
		}
		console.groupEnd();
	}
}
